import { Bell } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { NoticeCard, useNoticeAction } from '@/components/notices/NoticeCard';
import {
  markRead,
  markSeen,
  refreshNotices,
  useNoticePolling,
  useNotices
} from '@/components/notices/api';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';

import { useFeatures } from '@/hooks/useFeatures';

const LATEST = 6;

/**
 * The bell, left of the avatar. Desktop opens a popover with the latest cards
 * and "See all"; a phone goes straight to the full page, because a popover
 * over a 375px screen is a page that cannot scroll properly.
 *
 * Opening it clears the badge (the cards themselves stay highlighted until
 * clicked). Hidden entirely while the `notices` flag is off.
 */
export default function NoticesBell() {
  const { features, loading } = useFeatures();
  const enabled = !loading && !!features.notices;
  useNoticePolling(enabled);
  const { items, unseen } = useNotices();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { run, dialog } = useNoticeAction(() => setOpen(false));

  if (!enabled) return null;

  const badge = unseen > 9 ? '9+' : unseen > 0 ? String(unseen) : null;
  const label = unseen
    ? `Notifications, ${unseen} new`
    : 'Notifications';

  const trigger = (
    <Button
      id="notices-bell"
      variant="ghost"
      size="icon"
      className="relative text-muted-foreground hover:text-foreground"
      aria-label={label}
    >
      <Bell className="!size-5" />
      {badge ? (
        <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground">
          {badge}
        </span>
      ) : null}
    </Button>
  );

  return (
    <>
      {/* Phones: straight to the page. */}
      <span className="sm:hidden" onClick={() => navigate('/notifications')}>
        {trigger}
      </span>
      <span className="hidden sm:contents">
        <Popover
          open={open}
          onOpenChange={(o) => {
            setOpen(o);
            if (o) {
              refreshNotices().then(markSeen);
            }
          }}
        >
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent align="end" className="w-[400px] p-0">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <p className="text-sm font-semibold">Notifications</p>
              {items?.some((i) => i.unread) ? (
                <button
                  type="button"
                  className="text-xs font-medium text-primary hover:underline"
                  onClick={() => markRead('all')}
                >
                  Mark all as read
                </button>
              ) : null}
            </div>
            <div className="max-h-[60vh] divide-y overflow-y-auto">
              {items === null ? (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Loading…
                </p>
              ) : items.length === 0 ? (
                <Empty />
              ) : (
                items
                  .slice(0, LATEST)
                  .map((n) => (
                    <NoticeCard key={n.id} n={n} onAction={run} compact />
                  ))
              )}
            </div>
            <div className="border-t px-4 py-2.5 text-center">
              <button
                type="button"
                className="text-xs font-medium text-primary hover:underline"
                onClick={() => {
                  setOpen(false);
                  navigate('/notifications');
                }}
              >
                See all notifications
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </span>
      {dialog}
    </>
  );
}

export function Empty() {
  return (
    <div className="px-6 py-10 text-center">
      <Bell className="mx-auto mb-3 size-6 text-muted-foreground/60" />
      <p className="text-sm font-medium">You're all caught up</p>
      <p className="mt-1 text-xs text-muted-foreground">
        We'll let you know here if a report doesn't go out, your data stops
        updating, or something in your figures is worth a look.
      </p>
    </div>
  );
}
